import React, { useState, useEffect } from 'react';
import ImageUploadBox from '../../components/ImageUploadBox';
import { createNotification } from '../../services/notificationService';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, Typography, RadioGroup,
  FormControlLabel, Radio, makeStyles, CircularProgress,
  Snackbar, IconButton, Box, Divider, Checkbox
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import ScheduleIcon from '@material-ui/icons/Schedule';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import WebIcon from '@material-ui/icons/Web';

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    '& .MuiDialog-paper': {
      margin: 24,
      width: 'calc(100vw - 48px)',
      maxWidth: 'calc(100vw - 48px) !important',
      height: 'calc(100vh - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      borderRadius: 20,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.type === 'dark' ? '#0f172a' : '#ffffff',
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 25px 70px rgba(0,0,0,0.35)',
      [theme.breakpoints.down('xs')]: {
        margin: 8,
        width: 'calc(100vw - 16px)',
        maxWidth: 'calc(100vw - 16px) !important',
        height: 'calc(100vh - 16px)',
        maxHeight: 'calc(100vh - 16px)',
        borderRadius: 14,
      },
    },
  },
  dialogHeader: {
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? 'rgba(30, 41, 59, 0.8)' : 'rgba(248, 250, 252, 0.9)',
    backdropFilter: 'blur(10px)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  headerIconBadge: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)',
  },
  dialogTitle: {
    margin: 0,
    fontWeight: 700,
    fontSize: '1.25rem',
    color: theme.palette.text.primary,
  },
  dialogSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
  },
  closeBtn: {
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#ef4444',
    },
  },
  dialogContent: {
    padding: 24,
    flex: 1,
    overflowY: 'auto',
    background: theme.palette.type === 'dark' ? '#0b1120' : '#f8fafc',
  },
  sectionCard: {
    background: theme.palette.type === 'dark' ? '#1e293b' : '#ffffff',
    borderRadius: 16,
    padding: 24,
    marginBottom: 24,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: theme.palette.primary.main,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  dialogActions: {
    padding: '16px 24px',
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? '#1e293b' : '#ffffff',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 16,
  },
  promptDialog: {
    '& .MuiDialog-paper': {
      borderRadius: 20,
      padding: theme.spacing(2),
      maxWidth: 480,
    },
  },
}));

export default function NotificationFormModal({
  open,
  initialData = null,
  onClose,
  onSuccess,
  onOpenWebNotification,
}) {
  const classes = useStyles();

  const [sending, setSending] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [scheduleMode, setScheduleMode] = useState('now');
  const [promptWebDialog, setPromptWebDialog] = useState(false);
  const [sentNotificationData, setSentNotificationData] = useState(null);

  const [form, setForm] = useState({
    title: '',
    body: '',
    imageUrl: '',
    actionUrl: '',
    buttonText: '',
    scheduledDate: '',
    saveToGallery: true,
  });

  useEffect(() => {
    if (open) {
      if (initialData) {
        setForm({
          title: initialData.title || '',
          body: initialData.body || '',
          imageUrl: initialData.imageUrl || '',
          actionUrl: initialData.actionUrl || initialData.data?.actionUrl || '',
          buttonText: initialData.buttonText || initialData.data?.buttonText || '',
          scheduledDate: '',
          saveToGallery: true,
        });
      } else {
        setForm({
          title: '',
          body: '',
          imageUrl: '',
          actionUrl: '',
          buttonText: '',
          scheduledDate: '',
          saveToGallery: true,
        });
      }
      setScheduleMode('now');
      setPromptWebDialog(false);
      setSentNotificationData(null);
    }
  }, [open, initialData]);

  const handleSend = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!form.title.trim() || !form.body.trim()) {
      setSnackbar({ open: true, message: 'Title and message body are required.' });
      return;
    }

    if (scheduleMode === 'later' && !form.scheduledDate) {
      setSnackbar({ open: true, message: 'Please select a scheduled date and time.' });
      return;
    }

    setSending(true);
    try {
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        imageUrl: form.imageUrl.trim() || null,
        actionUrl: form.actionUrl.trim() || null,
        buttonText: form.buttonText.trim() || null,
        scheduledAt: scheduleMode === 'later' ? form.scheduledDate : null,
        saveToGallery: form.saveToGallery !== false,
      };

      const docId = await createNotification(payload);

      setSnackbar({
        open: true,
        message: scheduleMode === 'later'
          ? 'Notification scheduled successfully!'
          : 'Notification published successfully!',
      });

      setSentNotificationData({ id: docId, ...payload });
      setPromptWebDialog(true);
    } catch (err) {
      console.error('Failed to create notification:', err);
      setSnackbar({ open: true, message: 'Failed to send notification. Please try again.' });
    }
    setSending(false);
  };

  const handleSkipWebNotification = () => {
    setPromptWebDialog(false);
    if (onSuccess) onSuccess();
  };

  const handleProceedToWebNotification = () => {
    const data = sentNotificationData;
    setPromptWebDialog(false);
    if (onSuccess) onSuccess();
    if (onOpenWebNotification) onOpenWebNotification(data);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => !sending && onClose()}
        className={classes.dialogRoot}
        fullWidth
        maxWidth={false}
        scroll="paper"
      >
        <div className={classes.dialogHeader}>
          <div className={classes.headerLeft}>
            <div className={classes.headerIconBadge}>
              <NotificationsActiveIcon />
            </div>
            <div>
              <Typography className={classes.dialogTitle}>
                {initialData ? 'Resend / Reuse Notification' : 'Send Push Notification'}
              </Typography>
              <Typography className={classes.dialogSubtitle}>
                Compose and broadcast push notifications to mobile app users
              </Typography>
            </div>
          </div>
          <IconButton className={classes.closeBtn} onClick={() => !sending && onClose()} disabled={sending} size="small">
            <CloseIcon />
          </IconButton>
        </div>

        <DialogContent className={classes.dialogContent} dividers>
          <div className={classes.sectionCard}>
            <Typography className={classes.sectionTitle}>
              1. NOTIFICATION CONTENT
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Title *"
                  value={form.title}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter notification title..."
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  label="Body *"
                  value={form.body}
                  onChange={(e) => setForm(prev => ({ ...prev, body: e.target.value }))}
                  placeholder="Enter message content..."
                  required
                />
              </Grid>
            </Grid>
          </div>

          <div className={classes.sectionCard}>
            <Typography className={classes.sectionTitle}>
              2. NOTIFICATION MEDIA & ACTION
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                  Notification Image (Firebase Storage)
                </Typography>
                <ImageUploadBox
                  value={form.imageUrl}
                  onChange={(url) => setForm(prev => ({ ...prev, imageUrl: url }))}
                  folder="notification_images"
                  storagePath="notification_images"
                  initialUrl={form.imageUrl}
                  onUploadComplete={(url) => setForm(prev => ({ ...prev, imageUrl: url }))}
                  aspectRatio="16:9"
                  helperText="Recommended: 16:9 ratio, max 2MB. Optional."
                />
                {form.imageUrl && (
                  <Box mt={0.5} display="flex" alignItems="center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.saveToGallery !== false}
                          onChange={(e) => setForm(prev => ({ ...prev, saveToGallery: e.target.checked }))}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" style={{ color: '#10b981', fontWeight: 600, fontSize: '0.85rem' }}>
                          Auto-store image into Image Gallery (ছবিটি গ্যালারিতেও সংরক্ষণ করুন)
                        </Typography>
                      }
                    />
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Action URL (Deep link or Web URL)"
                  value={form.actionUrl}
                  onChange={(e) => setForm(prev => ({ ...prev, actionUrl: e.target.value }))}
                  placeholder="e.g., /quran/1 or https://..."
                  helperText="Opens when the user taps the notification."
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Button Text"
                  value={form.buttonText}
                  onChange={(e) => setForm(prev => ({ ...prev, buttonText: e.target.value }))}
                  placeholder="e.g., Read More, Open Surah"
                  helperText="Call-to-action text displayed on the notification."
                />
              </Grid>
            </Grid>
          </div>

          <div className={classes.sectionCard}>
            <Typography className={classes.sectionTitle}>
              3. DELIVERY SCHEDULE
            </Typography>
            <RadioGroup
              row
              value={scheduleMode}
              onChange={(e) => setScheduleMode(e.target.value)}
              style={{ marginBottom: 16 }}
            >
              <FormControlLabel
                value="now"
                control={<Radio color="primary" />}
                label={
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <SendIcon fontSize="small" color="primary" />
                    <Typography variant="body2" style={{ fontWeight: 600 }}>Send Immediately</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="later"
                control={<Radio color="primary" />}
                label={
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <ScheduleIcon fontSize="small" color="primary" />
                    <Typography variant="body2" style={{ fontWeight: 600 }}>Schedule for Later</Typography>
                  </Box>
                }
              />
            </RadioGroup>

            {scheduleMode === 'later' && (
              <Box mt={2}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  variant="outlined"
                  label="Delivery Date & Time *"
                  value={form.scheduledDate}
                  onChange={(e) => setForm(prev => ({ ...prev, scheduledDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: new Date().toISOString().slice(0, 16),
                  }}
                  helperText="Select when the notification should be dispatched."
                />
              </Box>
            )}
          </div>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button onClick={onClose} disabled={sending} style={{ borderRadius: 10, textTransform: 'none', marginRight: 8 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={sending ? <CircularProgress size={18} color="inherit" /> : (scheduleMode === 'later' ? <ScheduleIcon /> : <SendIcon />)}
            onClick={handleSend}
            disabled={sending}
            style={{
              borderRadius: 10,
              textTransform: 'none',
              fontWeight: 600,
              padding: '8px 24px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#ffffff',
            }}
          >
            {sending ? 'Processing...' : (scheduleMode === 'later' ? 'Schedule Notification' : 'Send Immediately')}
          </Button>
        </DialogActions>

        <Snackbar
          open={snackbar.open}
          message={snackbar.message}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Dialog>

      {/* Web Notification Prompt Dialog */}
      <Dialog
        open={promptWebDialog}
        onClose={handleSkipWebNotification}
        className={classes.promptDialog}
      >
        <DialogTitle style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
          <WebIcon style={{ color: '#10b981' }} />
          Publish to Noor Web as well?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ lineHeight: 1.6 }}>
            Your push notification was created successfully! Would you also like to store this announcement on <strong>Noor Web</strong> so web visitors can read it in the notification center?
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', gap: 8 }}>
          <Button onClick={handleSkipWebNotification} color="default" style={{ borderRadius: 10, textTransform: 'none' }}>
            No, Skip
          </Button>
          <Button
            onClick={handleProceedToWebNotification}
            variant="contained"
            color="primary"
            style={{
              borderRadius: 10,
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#ffffff',
            }}
          >
            Yes, Store for Web
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
